import type { FAQItem } from './types';

export const governanceFAQs: FAQItem[] = [
  {
    id: 'digital-vs-analog-governance',
    question: 'Should we use our existing governance structure or adopt your digital model?',
    answer: `While we can mirror your existing analog governance structure, our design goal is a digital governance model that is more effective for most communities. Here's the intent, most of which is still on the roadmap:

**Digital Governance Design Goals:**
• **Digitally Enforceable (planned):** Decisions recorded with a complete audit trail and automatically implementable
• **Seamless Integration (planned):** Voting, proposals, and implementation working together in one system
• **Built-in Dispute Resolution (planned):** Audit trails and cross-referencing to reduce conflicts
• **Cost Effective:** No expensive analog-to-digital translation or ongoing dual-system maintenance
• **Future Proof:** Designed specifically for modern digital community management

**Analog Governance Migration Challenges:**
• **Dual Authority Issues:** Constant conflicts between digital system capabilities and paper bylaws
• **Expensive Translation:** Converting cumbersome analog bylaws into programmatic code is complex and costly
• **Ongoing Complexity:** Residents and board members must constantly cross-reference between digital and analog systems
• **Limited Enforceability:** Analog authority cannot be digitally automated, creating implementation gaps

Ask us for current build status on the specific governance features you need before committing to a migration approach.`,
    category: 'governance',
    featured: true,
  },
  {
    id: 'legal-compliance',
    question: 'Is digital voting legal for our HOA?',
    answer:
      'Digital voting is legally compliant in most states when properly implemented, and our design targets legal requirements for notice, voting procedures, and record keeping. We strongly recommend reviewing with your HOA attorney to confirm compliance with your specific state laws and community bylaws, and confirming current build status with us, before implementation.',
    category: 'governance',
    featured: true,
  },
  {
    id: 'governance-migration-options',
    question: 'What are our options for implementing governance on your platform?',
    answer: `You have two implementation approaches, though we recommend the first once the digital governance features are built:

**Option 1: Adopt ToolboxLogic Digital Governance (Recommended, in progress)**
• Our digital governance model, once built
• Planned automation and enforcement features
• Lower implementation costs and faster deployment
• Planned conflict resolution and audit capabilities
• Seamless integration between voting, proposals, and implementation (planned)

**Option 2: Governance Mirroring (Not Recommended)**
• Mirror your existing analog governance structure into our platform
• Requires expensive analog-to-digital translation services
• Creates dual authority between digital platform and analog bylaws
• Ongoing complexity requiring constant cross-referencing
• Limited automation capabilities due to analog constraints

Ask us for current build status before choosing an approach.`,
    category: 'governance',
    featured: true,
  },
  {
    id: 'bylaw-integration',
    question: 'How do you handle our existing bylaws and governing documents?',
    answer: `During implementation, the plan is to map your existing governance documents into the platform:

• **Voting requirements** (quorum, approval thresholds, notice periods)
• **Meeting procedures** (notice requirements, agenda management)
• **Board structure** (roles, responsibilities, term limits)
• **Assessment and fee policies** (due dates, late fees, payment rules)
• **Facility usage rules** (booking policies, fees, restrictions)

Automatic enforcement of these rules is a design goal, not a built feature today. Ask us for current build status on any specific rule you need enforced before go-live.`,
    category: 'governance',
  },
  {
    id: 'board-management',
    question: 'What tools is the platform designed to provide for board members?',
    answer: `Board governance tools are a design goal; none of the following persists in the live product today.

**Decision Making (planned):**
• Create proposals for community voting
• Manage board elections
• Track voting participation and results
• Maintain audit trails for votes

**Meeting Management (planned):**
• Schedule meetings with automatic notifications
• Create and distribute agendas
• Record and store meeting minutes
• Track action items and follow-up

**Financial Oversight (planned):**
• Budget tracking and reporting
• Approve expenses and payments
• Monitor community financial health
• Generate reports for annual meetings

Ask us for current build status before relying on any of these for a board decision.`,
    category: 'governance',
  },
  {
    id: 'compliance-tracking',
    question: 'How is the platform designed to help with HOA compliance and record keeping?',
    answer: `Comprehensive compliance support is a design goal, not a built feature today:

• **Document vault** with version control for all governing documents (planned)
• **Meeting records** with searchable minutes and attendance (planned)
• **Financial records** with complete transaction history (planned)
• **Voting records** with audit trails (planned)
• **Communication logs** showing all official notices sent (planned)
• **Export capabilities** for external audits or legal requirements (planned)

Ask us for current build status before relying on any of these for a specific compliance or audit need.`,
    category: 'governance',
  },
  {
    id: 'admin-user-limits',
    question: 'How many administrator accounts can we have?',
    answer: `Resident users are unlimited across tiers.

Administrative users by tier:
• Silver: up to 25 admin users
• Gold: up to 100 admin users
• Enterprise: custom limits per agreement

These limits help maintain security and responsibility boundaries.`,
    category: 'governance',
  },
];
